
import {
  defineComponent,
  computed,
  ref,
  watch,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import type { SpmFieldConfig, SpmActiveSort } from '../../types';

export default defineComponent({
  name: 'SpmSortInput',

  components: {
    Dropdown,
  },

  props: {
    sortableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<SpmActiveSort | null>,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Field options for dropdown (use label if available, fallback to name)
    const fieldOptions = computed(() => props.sortableFields.map((field) => ({
      value: field.name,
      label: field.label || field.name,
    })));

    // Selected field
    const selectedField = ref<string | null>(props.modelValue?.field ?? null);

    // Selected order
    const selectedOrder = ref<'asc' | 'desc'>(props.modelValue?.order ?? 'asc');

    // Watch for external changes
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          selectedField.value = newVal.field;
          selectedOrder.value = newVal.order;
        } else {
          selectedField.value = null;
          selectedOrder.value = 'asc';
        }
      },
      { immediate: true },
    );

    // Emit changes
    const emitChange = () => {
      if (selectedField.value) {
        emit('update:modelValue', {
          field: selectedField.value,
          order: selectedOrder.value,
        });
      } else {
        emit('update:modelValue', null);
      }
    };

    watch(selectedField, emitChange);
    watch(selectedOrder, emitChange);

    return {
      t,
      fieldOptions,
      selectedField,
      selectedOrder,
    };
  },
});
